import deepEqual from 'deep-equal';

export type UnsavedFormFields = {
  isDirty?: boolean;
  sources?: unknown;
  media?: unknown;
  slideTitle?: string;
  slideSubTitle?: string;
  slideDescription?: string;
};

/**
 * Overlay only sources/media from the live ref. Those fields are written to the
 * ref synchronously in ``handleUnifiedSourcesChanges`` before React re-renders.
 * Spreading the entire ref over ``formData`` would clobber in-render updates
 * (e.g. ``isTimeValid`` or the Event Shown ``show`` toggle) because the ref is
 * otherwise synced in an effect after paint — which left Save disabled while
 * Cancel still saw unsaved changes.
 */
export function mergeLiveMediaIntoForm<T extends UnsavedFormFields>(
  formData: T | undefined,
  refValue: T | null | undefined,
): T | undefined {
  if (!formData) return formData;
  return {
    ...formData,
    sources: refValue?.sources ?? formData.sources,
    media: refValue?.media ?? formData.media,
  };
}

export function formSnapshotForUnsavedCheck<T extends UnsavedFormFields>(
  formData: T | undefined,
  refValue: T | null | undefined,
  slideDescription: string | undefined,
): T | undefined {
  const merged = mergeLiveMediaIntoForm(formData, refValue);
  if (!merged) return merged;
  return {
    ...merged,
    slideDescription: slideDescription ?? merged.slideDescription,
    slideTitle: formData?.slideTitle,
    slideSubTitle: formData?.slideSubTitle,
  };
}

export const stripDirty = <T extends { isDirty?: boolean }>(
  x: T | null | undefined,
): Omit<T, 'isDirty'> | null | undefined => {
  if (x === undefined) return undefined;
  if (x === null) return null;
  const { isDirty: _omit, ...rest } = x;
  return rest;
};

export const hasUnsavedChanges = <T extends { isDirty?: boolean }>(
  formData: T | undefined,
  baseline: T | null,
): boolean => !deepEqual(stripDirty(formData), stripDirty(baseline));

/**
 * Hydration (``markDirty: false``) may advance the unsaved-changes baseline so
 * auto-previews and enrichment do not prompt on close. If the form is already
 * dirty, replacing that baseline with live sources/media would treat in-flight
 * user edits as clean (Save disabled, Cancel discards silently).
 */
export function nextHydrationBaseline<T extends UnsavedFormFields>(
  baseline: T | null,
  nextFields: Pick<T, 'sources' | 'media'>,
  alreadyDirty: boolean,
): T | null {
  if (!baseline || alreadyDirty) return baseline;
  return { ...baseline, ...nextFields };
}

export function formIsDirtyAgainstBaseline<T extends UnsavedFormFields>(
  formData: T | undefined,
  refValue: T | null | undefined,
  baseline: T | null,
): boolean {
  if (!baseline) return false;
  return hasUnsavedChanges(
    formSnapshotForUnsavedCheck(formData, refValue, formData?.slideDescription),
    baseline,
  );
}
